import { authedFetch } from '@/providers/loginWithEmail/dataProvider';
import {
    ProjectFlow,
    ProjectFlowStep,
    ProjectFlowStepComment,
    CompletionCheckResponse,
    CompleteProjectResponse,
} from '@/types';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    let response: Response;
    try {
        response = await authedFetch(path, { ...init, headers });
    } catch (error) {
        if (
            (error instanceof DOMException || error instanceof Error) &&
            error.name === 'AbortError'
        ) {
            throw error;
        }
        throw new Error('Network unavailable. Try again when online.');
    }
    if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const message = extractErrorMessage(payload);
        throw new Error(message || 'Project flow request failed');
    }
    if (response.status === 204) {
        return undefined as T;
    }
    const text = await response.text();
    if (!text) return undefined as T;
    try {
        return JSON.parse(text) as T;
    } catch {
        return undefined as T;
    }
}

function extractErrorMessage(payload: any): string | null {
    if (!payload || typeof payload !== 'object') return null;
    // FastAPI default: { detail: string | object }
    if (typeof payload.detail === 'string') return payload.detail;
    if (payload.detail && typeof payload.detail === 'object') {
        const m = extractErrorsShape(payload.detail);
        if (m) return m;
    }
    // HTTPReactException: { errors: string | object }
    if (typeof payload.errors === 'string') return payload.errors;
    if (payload.errors && typeof payload.errors === 'object') {
        const m = extractErrorsShape(payload.errors);
        if (m) return m;
    }
    return null;
}

function extractErrorsShape(errors: any): string | null {
    // { root: { serverError: "..." } }
    const root = errors?.root;
    if (typeof root === 'string') return root;
    if (root && typeof root.serverError === 'string') return root.serverError;
    // First string value in the error map (field-level messages)
    for (const value of Object.values(errors)) {
        if (typeof value === 'string') return value;
        if (value && typeof value === 'object' && typeof (value as any).message === 'string') {
            return (value as any).message;
        }
    }
    return null;
}

export const fetchProjectFlow = (
    projectId: number,
    options?: { includeComments?: boolean }
): Promise<ProjectFlow> => {
    let path = `/projects/${projectId}/flow`;
    if (options?.includeComments) {
        path += '?includes=project_flow_step_comments';
    }
    return request<ProjectFlow>(path);
};

export const updateFlowStep = (
    projectId: number,
    stepId: number,
    body: { status?: string; name?: string }
): Promise<ProjectFlowStep> =>
    request<ProjectFlowStep>(`/projects/${projectId}/flow/steps/${stepId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });

export const createFlowStep = (
    projectId: number,
    body: { phase: string; name: string; after_step_id?: number }
): Promise<ProjectFlowStep> =>
    request<ProjectFlowStep>(`/projects/${projectId}/flow/steps`, {
        method: 'POST',
        body: JSON.stringify(body),
    });

export const deleteFlowStep = (projectId: number, stepId: number): Promise<void> =>
    request<void>(`/projects/${projectId}/flow/steps/${stepId}`, {
        method: 'DELETE',
    });

export const fetchStepComments = (
    projectId: number,
    stepId: number,
    options?: { limit?: number; offset?: number }
): Promise<ProjectFlowStepComment[]> => {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    const qs = params.toString();
    return request<ProjectFlowStepComment[]>(
        `/projects/${projectId}/flow/steps/${stepId}/comments${qs ? `?${qs}` : ''}`
    );
};

export const createStepComment = (
    projectId: number,
    stepId: number,
    content: string,
    files: File[]
): Promise<ProjectFlowStepComment> => {
    const form = new FormData();
    form.append('content', content);
    for (const file of files) {
        form.append('photos', file);
    }
    return request<ProjectFlowStepComment>(
        `/projects/${projectId}/flow/steps/${stepId}/comments`,
        {
            method: 'POST',
            body: form,
        }
    );
};

export const fetchCompletionCheck = (
    projectId: number
): Promise<CompletionCheckResponse> =>
    request<CompletionCheckResponse>(
        `/projects/${projectId}/flow/completion-check`
    );

export const completeProject = (
    projectId: number,
    force: boolean
): Promise<CompleteProjectResponse> =>
    request<CompleteProjectResponse>(`/projects/${projectId}/flow/complete`, {
        method: 'POST',
        body: JSON.stringify({ force }),
    });

export const deleteStepComment = (
    projectId: number,
    commentId: number
): Promise<void> =>
    request<void>(`/projects/${projectId}/flow/comments/${commentId}`, {
        method: 'DELETE',
    });

// Blob cache for local/relative photo URLs (presigned S3 URLs work directly in <img>)
const photoUrlCache = new Map<string, string>();

export async function resolvePhotoUrl(url: string): Promise<string> {
    // Absolute URLs (S3 presigned) return as-is
    if (url.startsWith('https://') || url.startsWith('http://')) {
        return url;
    }

    // Relative URLs (local backend) fetch as blob and cache object URL
    const cached = photoUrlCache.get(url);
    if (cached) return cached;

    const response = await authedFetch(url);
    if (!response.ok) {
        throw new Error('Could not load photo');
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    photoUrlCache.set(url, objectUrl);
    return objectUrl;
}

export function clearPhotoUrlCache(): void {
    photoUrlCache.forEach(url => URL.revokeObjectURL(url));
    photoUrlCache.clear();
}
